import { rootMargin } from '@helpers/animate';
import { Icon } from '@helpers/icons';
import { highlightTitleWords } from '@helpers/utils';
import { withMotion } from '@hoc/withMotion';
import { useInView } from '@hooks/useInView';
import { LinkButton } from '@stories/Components/Buttons/Button/Button';
import { AnimatePresence } from 'framer-motion';
import React from 'react';
import { LogoListStyles as S } from './LogoList.styles';
const LogoItem = ({ logo }) => {
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const imgRef = React.useRef(null);
    React.useEffect(() => {
        if (imgRef.current?.complete) {
            setImageLoaded(true);
        }
    }, [imgRef]);
    return (React.createElement(S.Logo, null,
        React.createElement(AnimatePresence, { exitBeforeEnter: true },
            React.createElement(S.LogoImage, { draggable: false, "data-loaded": imageLoaded, key: `LogoList_${logo.name}`, ref: imgRef, alt: logo.name, src: logo.image, onLoad: () => setImageLoaded(true), loading: "lazy" }))));
};
export const LogoGrid = (props) => {
    const [splitLogos, setSplitLogos] = React.useState([]);
    React.useEffect(() => {
        if (props.useReducedLayout) {
            return;
        }
        const logoRows = [];
        const rowLimit = props.rowLimit ?? 6;
        let iterator = 0;
        let groupSize = rowLimit - 1;
        while (iterator < (props.logoLimit ?? 33)) {
            // Take a slice of the logos //
            const startPoint = iterator % props.logos.length;
            const endPoint = (iterator + groupSize) % props.logos.length;
            let slice = [];
            if (startPoint > endPoint) {
                slice = props.logos
                    .slice(startPoint, props.logos.length)
                    .concat(props.logos.slice(0, endPoint));
            }
            else {
                slice = props.logos.slice(iterator % props.logos.length, (iterator + groupSize) % props.logos.length);
            }
            logoRows.push(slice.map((logo) => ({ ...logo })));
            iterator += groupSize;
            if (groupSize === rowLimit - 1) {
                groupSize = rowLimit;
            }
            else {
                groupSize = rowLimit - 1;
            }
        }
        setSplitLogos(logoRows);
    }, [props.logos, props.useReducedLayout]);
    const chooseNewRandomLogo = React.useCallback(() => {
        function getRandomItem() {
            const row = Math.floor(Math.random() * splitLogos.length);
            const col = Math.floor(Math.random() * splitLogos[row].length);
            return [row, col];
        }
        const [randomRow, randomCol] = getRandomItem();
        let randomLogoIndex = Math.floor(Math.random() * props.logos.length);
        while (props.logos[randomLogoIndex].image === splitLogos[randomRow][randomCol].image ||
            (randomCol === 0
                ? false
                : props.logos[randomLogoIndex].image === splitLogos[randomRow][randomCol - 1].image) ||
            (randomCol === splitLogos[randomRow].length - 1
                ? false
                : props.logos[randomLogoIndex].image === splitLogos[randomRow][randomCol + 1].image)) {
            randomLogoIndex = Math.floor(Math.random() * props.logos.length);
        }
        const clone = [...splitLogos];
        clone[randomRow][randomCol] = props.logos[randomLogoIndex];
        setSplitLogos(clone);
    }, [props.logos.length, splitLogos]);
    React.useEffect(() => {
        if (!props.useReducedLayout) {
            const interval = setInterval(chooseNewRandomLogo, 5000);
            return () => clearInterval(interval);
        }
    }, [splitLogos.length, props.useReducedLayout]);
    return (React.createElement(React.Fragment, null, props.useReducedLayout ? (React.createElement(React.Fragment, null,
        React.createElement(S.LogoGridCompact, { style: { '--count': props.logos.length } },
            props.logos.map((logo, i) => (React.createElement(LogoItem, { key: `LogoList_${i}`, logo: logo }))),
            props.logos.map((logo, i) => (React.createElement(LogoItem, { key: `LogoList_${i}_2`, logo: logo }))),
            props.logos.map((logo, i) => (React.createElement(LogoItem, { key: `LogoList_${i}_3`, logo: logo }))),
            props.logos.length < 10 && (React.createElement(React.Fragment, null, props.logos.map((logo, i) => (React.createElement(LogoItem, { key: `LogoList_${i}_4`, logo: logo }))))),
            props.logos.length < 5 && (React.createElement(React.Fragment, null, props.logos.map((logo, i) => (React.createElement(LogoItem, { key: `LogoList_${i}_5`, logo: logo })))))))) : (React.createElement(S.LogoGridWrapper, null,
        React.createElement(S.LogoGridInner, null, splitLogos.map((row, i) => (React.createElement(S.LogoGridRow, { key: `LogoList_${i}` }, row.map((logo, j) => (React.createElement(LogoItem, { key: `LogoList_${i}_${j}`, logo: logo })))))))))));
};
const LogoList = (props) => {
    const [containerRef, inView] = useInView({ rootMargin }, false, props.pageContext?.isEditMode);
    const useReducedLayout = React.useMemo(() => {
        return props.logos.length < 11;
    }, [props.logos.length]);
    return (React.createElement(S.MotionWrapper, { "data-in-view": inView },
        React.createElement(S.Container, { ref: containerRef, "data-use-reduced-layout": props.forceReduced || useReducedLayout, variant: props.variant ?? 'blue' },
            React.createElement(S.Inner, null,
                React.createElement(LogoGrid, { logos: props.logos, useReducedLayout: props.forceReduced || useReducedLayout }),
                React.createElement(S.ContentSection, null,
                    React.createElement(S.Title, { dangerouslySetInnerHTML: { __html: highlightTitleWords(props.title) } }),
                    props.content && React.createElement(S.Content, { dangerouslySetInnerHTML: { __html: props.content } }),
                    props.cta1 || props.cta2 ? (React.createElement(S.CTAsContainer, null,
                        props.cta1 && (React.createElement(LinkButton, { icon: props.cta1.icon, buttonType: "solid", target: props.cta1.target, href: props.cta1.url }, props.cta1.text)),
                        props.cta2 && (React.createElement(LinkButton, { icon: props.cta2.icon ?? Icon.None, buttonType: props.variant === 'white' ? 'text-black' : 'text-white', target: props.cta2.target, href: props.cta2.url }, props.cta2.text)))) : null)))));
};
export default withMotion(LogoList);
